import { useCallback, useEffect, useState } from 'react'
import type { CompetitionDetailView } from './types'

/**
 * One competition, with the caller's own entry. Not cached: joining, leaving
 * and seat counts all change it, and a stale card here would tell a member they
 * are in a competition they are not.
 */
export function useCompetition(
  competitionId: string | undefined,
  apiUrl: string,
  getAccessTokenSilently: () => Promise<string>,
) {
  const [competition, setCompetition] = useState<CompetitionDetailView | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  const reload = useCallback(async () => {
    if (!competitionId) return
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/my-competitions/${competitionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (res.ok) setCompetition((await res.json()) as CompetitionDetailView)
      else if (res.status === 404) setNotFound(true)
    } catch {
      // An expired session or a dropped network leaves the card as it was. The
      // effect below only ever calls this as a floating promise, so a rejection
      // that escapes here is an unhandled rejection, not an error state.
    } finally {
      setLoading(false)
    }
  }, [competitionId, apiUrl, getAccessTokenSilently])

  useEffect(() => {
    void reload()
  }, [reload])

  return { competition, loading, notFound, reload }
}
